/**
 * Display helpers for the Templates module — pure, so they can be read (and tested) without React.
 *
 * The one rule that matters here: **percentages are stored as fractions and shown as `%`** (plan §11.6).
 * Every conversion in the module goes through `toPercentDisplay` / `fromPercentInput`, which round away
 * the binary-floating-point noise that would otherwise turn 0.5 % into 0.49999999999999994.
 */
import type { ParamGroup, ParamUnit, TemplateParamDefinition, TemplateSummary, TemplateVersion } from 'used-vehicle-contracts';
import type { StatusTone } from '../../components/ui/StatusText';
import { formatCount, formatDays, formatDecimal, formatKm, formatMoney } from '../../lib/format';

/** The order the groups are drawn in, on both the detail page and the draft form. */
export const PARAM_GROUP_ORDER: readonly ParamGroup[] = ['FEES', 'DEPRECIATION', 'INSPECTION', 'WORK', 'TRADE_IN'];

/** The two damage tables have seven rows each, index 0 = "no finding". */
export const DAMAGE_GROUP_SIZE = 7;

/** A fraction key is edited as a percentage; everything else is edited as the number it is. */
export const isFractionUnit = (unit: ParamUnit): boolean => unit === 'fraction';

const round = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;

  return Math.round((value + Number.EPSILON) * factor) / factor;
};

/** `0.005` → `0.5`. Six decimals is far more than any rate the workbook states. */
export const toPercentDisplay = (fraction: number): number => round(fraction * 100, 6);

/** `0.5` → `0.005`. */
export const fromPercentInput = (percent: number): number => round(percent / 100, 8);

/** The step the `%` inputs use: one hundredth of a percentage point. */
export const PERCENT_STEP = 0.01;

/** One shared value, printed the way its unit asks to be read. */
export const formatParamValue = (value: number | undefined, unit: ParamUnit): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  switch (unit) {
    case 'fraction':
      return `${formatDecimal(toPercentDisplay(value), 4)} %`;
    case 'JOD':
      return formatMoney(value);
    case 'JOD/day':
      return `${formatMoney(value)} / day`;
    case 'JOD/L':
      return `${formatMoney(value)} / L`;
    case 'km':
      return formatKm(value);
    case 'km/year':
      return `${formatKm(value)} / year`;
    case 'days':
      return formatDays(value);
    case 'L':
      return `${formatDecimal(value, 2)} L`;
    default:
      return formatCount(value);
  }
};

export const versionStatusTone = (status: TemplateVersion['status']): StatusTone => {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'info';
    default:
      return 'muted';
  }
};

/** `Toyota Corolla · HYBRID` — the mono subline under a template's name. */
export const identitySubline = (template: Pick<TemplateSummary, 'make' | 'model' | 'fuel'>, fuelLabel: string): string =>
  `${template.make} ${template.model} · ${fuelLabel}`;

export const groupParams = (params: readonly TemplateParamDefinition[]): Map<ParamGroup, TemplateParamDefinition[]> => {
  const grouped = new Map<ParamGroup, TemplateParamDefinition[]>(PARAM_GROUP_ORDER.map((group) => [group, []]));

  for (const definition of params) {
    const bucket = grouped.get(definition.group);

    if (bucket) {
      bucket.push(definition);
    } else {
      grouped.set(definition.group, [definition]);
    }
  }

  return grouped;
};

export interface ValueIssue {
  key: string;
  code: string;
  detail: string;
}

/**
 * The per-field errors a 400 from `templates.updateDraftVersion` / `publishVersion` carries, indexed by
 * the key they belong to so the form can print each one under its own input.
 */
export const issuesByKey = (issues: readonly ValueIssue[]): Map<string, ValueIssue[]> => {
  const byKey = new Map<string, ValueIssue[]>();

  for (const issue of issues) {
    byKey.set(issue.key, [...(byKey.get(issue.key) ?? []), issue]);
  }

  return byKey;
};

/** Reads the `errors` array off an oRPC error payload, whatever else it carries. */
export const valueIssuesOf = (error: unknown): ValueIssue[] => {
  const data = (error as { data?: { errors?: unknown } } | undefined)?.data;

  if (!data || !Array.isArray(data.errors)) {
    return [];
  }

  return data.errors.filter(
    (issue): issue is ValueIssue =>
      typeof issue === 'object' &&
      issue !== null &&
      typeof (issue as ValueIssue).key === 'string' &&
      typeof (issue as ValueIssue).detail === 'string',
  );
};

/**
 * "Fees & commercial values" as the business review asked for it (2026-09-23, plan §11.19): one field per
 * row, split by what the money is for. Every FEES key of the catalogue appears in exactly one section; a
 * key added to the catalogue later and not listed here lands in `OTHER`, so nothing is ever hidden.
 */
export const FEE_SECTIONS: readonly { key: string; params: readonly string[] }[] = [
  { key: 'INSURANCE', params: ['insuranceRate', 'insuranceCap'] },
  { key: 'REGISTRATION', params: ['registrationFee', 'registrationThresholdDays', 'ownershipTransferRate'] },
  { key: 'LICENSING', params: ['licensingRate', 'generalFee'] },
  { key: 'SALES', params: ['commissionFee', 'overheadRate'] },
  { key: 'FUEL', params: ['fuelLiters', 'fuelPricePerLiter'] },
  { key: 'HOLDING', params: ['dailyHoldingRate', 'expectedHoldingDays'] },
  { key: 'PROFIT', params: ['targetProfit', 'targetMargin', 'riskBuffer'] },
  { key: 'ROUNDING', params: ['roundingIncrement'] },
];

export const feeSections = (definitions: readonly TemplateParamDefinition[]): { key: string; definitions: TemplateParamDefinition[] }[] => {
  const byKey = new Map(definitions.map((definition) => [definition.key, definition]));
  const listed = new Set(FEE_SECTIONS.flatMap((section) => section.params));
  const sections = FEE_SECTIONS.map((section) => ({
    key: section.key,
    definitions: section.params.map((param) => byKey.get(param)).filter((entry): entry is TemplateParamDefinition => entry !== undefined),
  }));
  const other = definitions.filter((definition) => !listed.has(definition.key));

  return [...sections, ...(other.length > 0 ? [{ key: 'OTHER', definitions: other }] : [])].filter((section) => section.definitions.length > 0);
};
